import React, { useEffect, useRef } from 'react';
import { Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { IExternalDecisionView, IExternalStageView } from 'shared/interfaces/formApplication';
import { ApplicationStatus } from 'shared/types/types';
import './ApprovalProgress.scss';

/**
 * #143: approval flow of the applicant's request, External view.
 * `bar` (phone/tablet, under the title): every stage in ONE horizontal row that scrolls
 * sideways when it does not fit. `sidebar` (laptop+, next to the form — like Internal's
 * ShowApprovalFlow): stages stacked vertically, scrolling vertically.
 * A stage with several approvers (sub-stages) is drawn as a bordered group, each approver
 * with its own decision. Stages the flow has not reached yet (`totalStages`) are upcoming.
 */

type StageState = 'approved' | 'denied' | 'pending' | 'upcoming';

interface ApprovalProgressProps {
    approvalFlow: IExternalStageView[];
    totalStages?: number;
    applicationStatus: ApplicationStatus;
    layout: 'bar' | 'sidebar';
}

const STATE_ICONS: Record<StageState, React.ReactNode> = {
    approved: <CheckCircleIcon fontSize="inherit" />,
    denied: <CancelIcon fontSize="inherit" />,
    pending: <AccessTimeIcon fontSize="inherit" />,
    upcoming: <RadioButtonUncheckedIcon fontSize="inherit" />,
};

const DECISION_LABELS: Record<string, string> = { approved: 'אישר/ה', denied: 'דחה/תה' };

const toStageState = (status: string | undefined): StageState =>
    status === 'approved' || status === 'denied' || status === 'pending' ? status : 'upcoming';

const formatDate = (date?: Date | null) => (date ? new Date(date).toLocaleDateString('he-IL') : '');

const statusText = (status: ApplicationStatus, currentStage: number | undefined, total: number): string => {
    switch (status) {
        case 'approved':
            return 'הבקשה אושרה';
        case 'denied':
            return 'הבקשה נדחתה';
        case 'returned':
            return 'הבקשה הוחזרה לתיקון';
        case 'cancelled':
            return 'הבקשה בוטלה';
        default:
            return currentStage && total ? `ממתינה לאישור — שלב ${currentStage} מתוך ${total}` : 'ממתינה לאישור';
    }
};

/** The approver's comment, in a simple block shown on hover (tap on touch screens). */
const CommentHint: React.FC<{ comment: string }> = ({ comment }) => (
    <Tooltip
        title={<div className="approval-comment-block">{comment}</div>}
        arrow
        enterTouchDelay={0}
        leaveTouchDelay={5000}
    >
        <button type="button" className="approval-comment-trigger" aria-label={`הערת המאשר: ${comment}`}>
            <ChatBubbleOutlineIcon fontSize="inherit" />
        </button>
    </Tooltip>
);

const DecisionItem: React.FC<{ decision: IExternalDecisionView; isSubStage: boolean }> = ({ decision, isSubStage }) => {
    const decisionState = toStageState(decision.decisionStatus);
    const isDecided = decisionState === 'approved' || decisionState === 'denied';
    return (
        <li className={`approval-decision approval-decision--${decisionState}`}>
            <span className="approval-decision-line">
                {isSubStage && (
                    <span className="approval-decision-icon" aria-hidden="true">
                        {STATE_ICONS[decisionState]}
                    </span>
                )}
                <span className="approval-decision-approver">{decision.approverTitle || 'מאשר/ת'}</span>
                {isDecided && decision.decisionComment && <CommentHint comment={decision.decisionComment} />}
            </span>
            {isDecided && (
                <span className="approval-decision-meta">
                    {DECISION_LABELS[decisionState]}
                    {decision.decisionUserName ? ` · ${decision.decisionUserName}` : ''}
                    {decision.decisionTimestamp ? ` · ${formatDate(decision.decisionTimestamp)}` : ''}
                </span>
            )}
        </li>
    );
};

export const ApprovalProgress: React.FC<ApprovalProgressProps> = ({ approvalFlow, totalStages, applicationStatus, layout }) => {
    const currentStageRef = useRef<HTMLLIElement>(null);

    const flow = [...(approvalFlow || [])].sort((stageA, stageB) => stageA.stageIndex - stageB.stageIndex);
    const total = Math.max(totalStages ?? 0, flow.length, ...flow.map((stage) => stage.stageIndex));
    const currentStage = flow.find((stage) => stage.stageStatus === 'pending')?.stageIndex;
    const stages = Array.from({ length: total }, (_, position) => {
        const stageIndex = position + 1;
        const stage = flow.find((flowStage) => flowStage.stageIndex === stageIndex);
        const state: StageState = stage ? toStageState(stage.stageStatus) : 'upcoming';
        return { stageIndex, state, decisions: stage?.decisions ?? [] };
    });

    // Long flows scroll: bring the START of the current stage into view (a stage with many
    // sub-stages can be wider than the bar, so centring it would hide its title).
    useEffect(() => {
        currentStageRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'start' });
    }, [currentStage, layout]);

    return (
        <section className={`approval-progress approval-progress--${layout}`} aria-label="סבב אישורים">
            <div className={`approval-progress-status approval-progress-status--${applicationStatus}`}>
                {statusText(applicationStatus, currentStage, total)}
            </div>
            {stages.length > 0 && (
                <ol className="approval-progress-stages">
                    {stages.map(({ stageIndex, state, decisions }) => {
                        const hasSubStages = decisions.length > 1;
                        return (
                            <li
                                key={stageIndex}
                                ref={stageIndex === currentStage ? currentStageRef : undefined}
                                className={`approval-stage approval-stage--${state}${hasSubStages ? ' approval-stage--grouped' : ''}`}
                                aria-current={state === 'pending' ? 'step' : undefined}
                            >
                                <span className="approval-stage-icon" aria-hidden="true">
                                    {STATE_ICONS[state]}
                                </span>
                                <div className="approval-stage-body">
                                    <span className="approval-stage-title">
                                        שלב {stageIndex}
                                        {hasSubStages && <span className="approval-stage-count"> ({decisions.length} מאשרים)</span>}
                                    </span>
                                    {decisions.length > 0 && (
                                        <ul className="approval-stage-decisions">
                                            {decisions.map((decision, decisionIndex) => (
                                                <DecisionItem
                                                    key={`${stageIndex}-${decisionIndex}-${decision.approverTitle ?? ''}`}
                                                    decision={decision}
                                                    isSubStage={hasSubStages}
                                                />
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </section>
    );
};

export default ApprovalProgress;
